import Checkbox from "@app/components/checkbox";
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import Button from "@app/components/button";
import { Images } from "@assets/images";
import CustomMultiReactSelect from "@app/components/custom-multi-react-select";
import { ChangeEvent, useCallback, useState } from "react";
import UploadMediaService from "@app/services/http/upload-media.service";
import { useFormikContext } from "formik";
import CustomAsyncMultiSelect from "@app/components/custom-async-multi-select";
import { UserTypeOptions } from "@app/constants";
import BookService from "@app/services/http/book.service";
import CampaignService from "@app/services/http/campaign.service";
import { LabelValue } from "@app/types";
import { addToast } from "@app/components/toast/toast.service";

const NotificationComponent = () => {
  const [notificationMediaFile, setNotificationMediaFile] =
    useState<File | null>(null);
  const [notificationMediaLink, setNotificationMediaLink] =
    useState<string>("");
  const formik = useFormikContext<any>();
  const [isTypeUser, setIsTypeUser] = useState(false);
  const [isTestLoading, setIsTestLoading] = useState(false);

  const handleTypeUserChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsTypeUser(e.target.checked);
  };

  const handleTestNotification = async () => {
    if (
      !formik.values.notificationTestingIds ||
      !formik.values.notificationTitle ||
      !formik.values.notificationBody
    ) {
      return;
    }

    setIsTestLoading(true);
    try {
      await CampaignService.pushNotificationTest({
        title: formik.values.notificationTitle,
        body: formik.values.notificationBody,
        rightIcon: formik.values.notificationRightIcon || "",
        testingIds: formik.values.notificationTestingIds,
      });
      addToast({ text: "Gửi thông báo test thành công!" });
    } catch (error) {
      addToast({ text: "Gửi thông báo test thất bại" });
    } finally {
      setIsTestLoading(false);
    }
  };

  const fetchUserOptions = async (inputValue: string) => {
    return UserTypeOptions.filter((option) => option.value !== "ALL").filter(
      (option) => option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  const fetchBookOptions = useCallback(async (inputValue: string) => {
    const res = await BookService.getRetailBooks(
      { keyword: inputValue, pageNum: 1, pageSize: 20 },
      undefined,
      false
    );

    return (res.data?.items || []).map((it: LabelValue) => ({
      label: it.label,
      value: it.value,
    }));
  }, []);

  const repeatDayOptions = [
    { label: "Thứ 2", value: "Thứ 2" },
    { label: "Thứ 3", value: "Thứ 3" },
    { label: "Thứ 4", value: "Thứ 4" },
    { label: "Thứ 5", value: "Thứ 5" },
    { label: "Thứ 6", value: "Thứ 6" },
    { label: "Thứ 7", value: "Thứ 7" },
    { label: "Chủ nhật", value: "Chủ nhật" },
  ];

  return (
    <div className="w-[100%] max-w-[700px]">
      <div className="text-[#343C6A] font-medium mb-2">Thông báo</div>
      <div className="bg-white py-6 px-5 rounded-xl">
        <div>
          <label className="text-sm text-[#343C6A] font-medium mb-2 block">
            Tiêu đề thông báo
          </label>
          <FormControl name="notificationTitle">
            <Input placeholder="Nhập tiêu đề thông báo" />
          </FormControl>
        </div>
        <div className="mt-7">
          <label className="text-sm text-[#343C6A] font-medium mb-2 block">
            Các user id test
          </label>
          <FormControl name="notificationTestingIds">
            <Input placeholder="Nhập ID, cách nhau dấu phẩy" />
          </FormControl>
          <div className="text-xs text-[#83898C] mt-1">
            Nhập các userId cách nhau bởi dấu &quot;,&quot;. Ex: 1,2,3
          </div>
        </div>
        <div className="mt-7">
          <label className="text-sm text-[#343C6A] font-medium mb-2 block">
            Nội dung thông báo
          </label>
          <FormControl name="notificationBody">
            <Input placeholder="Nhập nội dung thông báo" />
          </FormControl>
        </div>

        {/* Upload or Link - interactive */}
        <div className="mt-6">
          <label className="text-sm text-[#343C6A] font-medium mb-2 block">
            Hình ảnh thông báo
          </label>

          {/* Image Preview */}
          {(formik.values.notificationRightIcon || notificationMediaFile) && (
            <div className="mb-4">
              <img
                src={
                  notificationMediaFile
                    ? URL.createObjectURL(notificationMediaFile)
                    : formik.values.notificationRightIcon
                }
                alt="Preview"
                className="w-32 h-32 object-cover rounded-lg border border-gray-200"
              />
            </div>
          )}

          <div className="flex items-center gap-5">
            <label className="flex-1 bg-[rgba(239,241,249,0.6)] rounded-lg px-4 py-2 w-[207px] h-[52px] cursor-pointer flex items-center justify-between">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files && e.target.files[0];

                  setNotificationMediaFile(file ?? null);

                  if (file) {
                    try {
                      const res = await UploadMediaService.upload({
                        medias: [file],
                      });
                      const url = res.data?.items?.[0]?.url || "";

                      if (url) {
                        formik.setFieldValue("notificationRightIcon", url);
                      }
                    } catch (error) {
                      // swallow error, keep UX simple
                    }
                  }
                }}
              />
              <div className="text-[#ABAFB1] text-base truncate mr-3">
                {notificationMediaFile?.name || "Upload"}
              </div>
              <img
                src={Images.UploadIcon.default}
                alt="upload"
                className="w-6 h-6"
              />
            </label>
            <div className="text-[#83898C] text-sm">or</div>
            <div className="flex-1 bg-[rgba(239,241,249,0.6)] rounded-lg px-4 py-2 w-[207px] h-[52px] flex items-center gap-3">
              <input
                type="text"
                placeholder="Link"
                value={notificationMediaLink}
                onChange={(e) => {
                  setNotificationMediaLink(e.target.value);
                }}
                className="flex-1 bg-transparent outline-none text-[#ABAFB1] placeholder:text-[#ABAFB1] text-base"
              />
              <img
                src={Images.LinkIcon.default}
                alt="link"
                className="w-6 h-6"
              />
            </div>
          </div>
        </div>

        <div className="mt-7">
          <label className="text-sm text-[#343C6A] font-medium mb-2 block">
            Thời gian gửi
          </label>
          <FormControl name="notificationTimeSend">
            <Input type="time" placeholder="Chọn thời gian gửi" />
          </FormControl>
        </div>
        <div className="mt-3 flex items-center gap-4">
          <FormControl name="isRepeatNotification">
            <Checkbox label="Lặp lại" labelClassName="text-sm text-[#83898C]" />
          </FormControl>
        </div>
        <div className="mt-3 flex items-center gap-4">
          <Checkbox
            label="Loại user"
            labelClassName="text-sm text-[#83898C]"
            onChange={handleTypeUserChange}
            checked={isTypeUser}
          />
        </div>
        {isTypeUser && (
          <>
            <div className="w-[50%] mt-4">
              <label className="text-sm text-[#343C6A] font-medium mb-2 block">
                Loại user
              </label>
              <FormControl name="notificationUserType">
                <CustomAsyncMultiSelect
                  promiseOptions={fetchUserOptions}
                  className="rounded-lg"
                  placeholder="Chọn loại user"
                />
              </FormControl>
            </div>
            <div className="w-[50%] mt-4">
              <label className="text-sm text-[#343C6A] font-medium mb-2 block">
                User đã nghe trên số phút
              </label>
              <FormControl name="notificationUserListenedToMinutes">
                <Input type="number" placeholder="Nhập số phút" />
              </FormControl>
            </div>
            <div className="w-[50%] mt-4">
              <label className="text-sm text-[#343C6A] font-medium mb-2 block">
                Tài khoản kas của user đang lớn hơn
              </label>
              <FormControl name="notificationKasIsGreaterThan">
                <Input type="number" placeholder="Nhập số kas" />
              </FormControl>
            </div>
            <div className="w-[50%] mt-4">
              <label className="text-sm text-[#343C6A] font-medium mb-2 block">
                Tài khoản kas của user đang nhỏ hơn
              </label>
              <FormControl name="notificationKasIsLessThan">
                <Input type="number" placeholder="Nhập số kas" />
              </FormControl>
            </div>
            <div className="w-[100%] mt-4">
              <label className="text-sm text-[#343C6A] font-medium mb-2 block">
                User đang có sách lẻ
              </label>
              <FormControl name="notificationHasPurchasedBooks">
                <CustomAsyncMultiSelect
                  placeholder="Chọn sách user đã mua"
                  promiseOptions={fetchBookOptions as any}
                />
              </FormControl>
            </div>
            <div className="w-[100%] mt-4">
              <label className="text-sm text-[#343C6A] font-medium mb-2 block">
                User chưa có sách lẻ
              </label>
              <FormControl name="notificationNotHasPurchasedBooks">
                <CustomAsyncMultiSelect
                  placeholder="Chọn sách user chưa mua"
                  promiseOptions={fetchBookOptions as any}
                />
              </FormControl>
            </div>
          </>
        )}
        <div className="mt-3 flex items-center gap-4">
          <label htmlFor="">
            <FormControl name="notificationBirthday">
              <Checkbox
                label="Sinh nhật trong khoảng thời gian campaign"
                labelClassName="text-sm text-[#83898C]"
              />
            </FormControl>
          </label>
        </div>

        {formik.values.isRepeatNotification && (
          <div className="mt-3 w-[100%]">
            <label className="text-sm text-[#343C6A] font-medium mb-2 block">
              Chọn ngày lặp lại
            </label>
            <FormControl name="notificationRepeatDays">
              <CustomMultiReactSelect
                className="rounded-lg"
                placeholder="Chọn ngày lặp lại"
                options={repeatDayOptions}
              />
            </FormControl>
          </div>
        )}
      </div>
      {/* Test Notification Button */}
      <div className="mt-2">
        <Button
          label={isTestLoading ? "Đang gửi..." : "Gửi thông báo test"}
          disabled={
            !formik.values.notificationTestingIds ||
            !formik.values.notificationTitle ||
            !formik.values.notificationBody ||
            isTestLoading
          }
          onClick={handleTestNotification}
          width={180}
          size="s"
          theme="primary"
        />
      </div>
    </div>
  );
};

export default NotificationComponent;
